// 1) Official ISO 4217 codes (180+).
const iso4217Codes = [
    "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN", "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BOV", "BRL", "BSD", "BTN", "BWP", "BYN", "BZD",
    "CAD", "CDF", "CHE", "CHF", "CHW", "CLF", "CLP", "CNY", "COP", "COU", "CRC", "CUC", "CUP", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP", "ERN", "ETB", "EUR", "FJD", "FKP", "GBP",
    "GEL", "GHS", "GIP", "GMD", "GNF", "GTQ", "GYD", "HKD", "HNL", "HRK", "HTG", "HUF", "IDR", "ILS", "INR", "IQD", "IRR", "ISK", "JMD", "JOD", "JPY", "KES", "KGS", "KHR", "KMF", "KPW",
    "KRW", "KWD", "KYD", "KZT", "LAK", "LBP", "LKR", "LRD", "LSL", "LYD", "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MRU", "MUR", "MVR", "MWK", "MXN", "MXV", "MYR", "MZN", "NAD",
    "NGN", "NIO", "NOK", "NPR", "NZD", "OMR", "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG", "QAR", "RON", "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SDG", "SEK", "SGD", "SHP", "SLL",
    "SOS", "SRD", "SSP", "STN", "SVC", "SYP", "SZL", "THB", "TJS", "TMT", "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX", "USD", "USN", "UYI", "UYU", "UYW", "UZS", "VES", "VND",
    "VUV", "WST", "XAF", "XAG", "XAU", "XBA", "XBB", "XBC", "XBD", "XCD", "XDR", "XOF", "XPD", "XPF", "XPT", "XSU", "XTS", "XUA", "XXX", "YER", "ZAR", "ZMW", "ZWL"
];

// 2) Common currency symbols (escaped for regex).
// Common / widely recognized currency symbols (escaped for regex).
const currencySymbols = [
    '\\$',    // Dollar (generic) => used in many countries
    '€',      // Euro
    '£',      // British Pound
    '¥',      // Japanese Yen / Chinese Yuan
    '₩',      // South Korean Won
    '₹',      // Indian Rupee symbol
    '₽',      // Russian Ruble symbol
    '₺',      // Turkish Lira
    '₱',      // Philippine Peso
    '฿',      // Thai Baht
    '₦',      // Nigerian Naira
    '₵',      // Ghanaian Cedi
    '₴',      // Ukrainian Hryvnia
    'zł',     // Polish Zloty
    'kr',     // Krona/krone (Sweden, Norway, Denmark, etc.) - ambiguous
    'Kč',     // Czech Koruna
    'Ft',     // Hungarian Forint
    '₪',      // Israeli New Shekel
    '₫',      // Vietnamese Dong

    // Variations of $ with country/region initials:
    'R\\$',   // Brazilian Real symbol
    'C\\$',   // Canadian Dollar symbol
    'A\\$',   // Australian Dollar symbol
    'NZ\\$',  // New Zealand Dollar symbol
    'HK\\$',  // Hong Kong Dollar symbol
    'NT\\$',  // New Taiwan Dollar symbol
];

// 3) Major regional notations (escaped for regex).
// Major regional notations (escaped for regex).
const regionalSymbols = [
    // Various Peso notations
    'Col\\$',  // Colombian Peso
    'Mex\\$',  // Mexican Peso
    'Ar\\$',   // Argentine Peso
    'RD\\$',   // Dominican Peso
    'Ch\\$',   // Chilean Peso (sometimes "CH$")
    'U\\$S',   // Uruguayan Peso (often "U$S" in local usage)

    // Sol, Bolivar, etc.
    'S/.',     // Peruvian Sol
    'Bs\\.?',  // Bolivian or Venezuelan Bolivar (e.g. "Bs.", "BsF")

    // Shillings
    'KSh',     // Kenyan Shilling
    'TSh',     // Tanzanian Shilling
    'USh',     // Ugandan Shilling

    // Additional local notations
    'N\\$',    // Namibian Dollar
    'ZK',      // Zambian Kwacha
    'GH₵',     // Ghanaian Cedi (alternative notation)
    '₡',       // Costa Rican Colón
    '₲',       // Paraguayan Guaraní
];

// Merge them all into one array.
const knownCurrencies = [
    ...iso4217Codes,
    ...currencySymbols,
    ...regionalSymbols
];

/**
 * Extracts a currency + amount from text.
 * Handles either:
 *    (CURRENCY)(SPACE?)(NUMBER)
 * OR (NUMBER)(SPACE?)(CURRENCY).
 *
 * Returns:
 * {
 *   currency: string,
 *   amount: number,
 *   original: string,   // The exact matched numeric string (with commas/dots)
 *   position: 'before' | 'after'
 * }
 */
function extractCurrencyAndAmount(text) {
    // Build the big pattern with 'i' => case-insensitive for codes like "USD".
    const pattern = new RegExp(
        `(?:(` + knownCurrencies.join('|') + `)\\s*([\\d.,]+)|([\\d.,]+)\\s*(` + knownCurrencies.join('|') + `))`,
        'i'
    );

    // Attempt the match
    const match = text.match(pattern);
    if (!match) return null;

    // match[1] => currency if it appears before the amount
    // match[2] => the numeric string if currency was before
    // match[3] => the numeric string if currency was after
    // match[4] => currency if after
    const currency = match[1] || match[4];
    const rawNumber = match[2] || match[3];

    // Determine if the currency is before or after
    const currencyPosition = match[1] ? 'before' : 'after';

    // Parse the numeric string with our new function that preserves the main/decimal split
    const details = parseNumberWithSplit(rawNumber);

    // If parsing fails, return null
    if (isNaN(details.floatValue)) return null;

    // Construct final result
    return {
        currency,                  // e.g. "R$", "USD", etc.
        amount: details.floatValue,   // e.g. 1200.5
        originalNumber: rawNumber,    // e.g. "1.200,50"
        position: currencyPosition,             // 'before' or 'after'

        // new fields preserving original formatting:
        mainNumber: details.mainNumber,         // e.g. "1.200"
        decimalPart: details.decimalPart,       // e.g. ",50"
        decimalSeparator: details.decimalSeparator  // e.g. "," or "." or null
    };
}

/**
 * Parses a numeric string with possible commas/dots, returning:
 *   floatValue: number
 *   mainNumber: string    // Original formatting for the integer portion (keeps thousands separators)
 *   decimalPart: string   // Everything from the decimal separator onward (including it), or ""
 *   decimalSeparator: '.' | ',' | null
 *
 * Heuristic approach (similar to parseLocaleNumber):
 *   1) If both '.' and ',' exist, whichever appears last is the "decimal separator."
 *   2) If only one appears, use these rules:
 *      - If the part after it has 2 digits => decimal
 *      - If the part after it has 3 digits => thousands
 *      - Otherwise => decimal
 *   3) If no separators => purely integer
 *
 * Then we create a "cleaned" string for parseFloat (removing thousands) but keep
 * the original formatting in mainNumber and decimalPart.
 */
function parseNumberWithSplit(numStr) {
    const original = numStr.trim();
    if (!original) {
        return { floatValue: NaN, mainNumber: '', decimalPart: '', decimalSeparator: null };
    }

    // We'll figure out which index (if any) is the "decimal separator."
    // Then mainNumber = original.slice(0, decimalIndex)
    // decimalPart = original.slice(decimalIndex) (including the separator)
    // If no decimal, decimalPart = "".

    // Step 1: Check presence of '.' and ','
    const dotIndex = original.lastIndexOf('.');
    const commaIndex = original.lastIndexOf(',');

    let decimalIndex = -1;      // the position of the decimal separator in the original string
    let decimalSeparator = null; // '.' or ',' or null

    // We'll do the same logic as before but to *locate* the decimal index,
    // and then build a "cleaned" copy for parseFloat.
    let cleaned = '';

    // A) Both '.' and ',' exist
    if (dotIndex !== -1 && commaIndex !== -1) {
        // whichever is last is decimal
        if (dotIndex > commaIndex) {
            // '.' is last => '.' is decimal
            decimalIndex = dotIndex;
            decimalSeparator = '.';
            // cleaned => remove all commas
            cleaned = original.replace(/,/g, '');
        } else {
            // ',' is last => ',' is decimal
            decimalIndex = commaIndex;
            decimalSeparator = ',';
            // cleaned => remove all '.' then replace ',' with '.'
            cleaned = original.replace(/\./g, '');
            cleaned = cleaned.replace(',', '.');
        }
    }
    // B) Zero or One separator
    else {
        // B1) No separator => purely integer
        if (dotIndex === -1 && commaIndex === -1) {
            decimalIndex = -1;
            decimalSeparator = null;
            cleaned = original; // just digits
        }
        // B2) Exactly one separator
        else {
            const sepIsDot = (dotIndex !== -1);
            const sepPos = sepIsDot ? dotIndex : commaIndex;
            const sepChar = sepIsDot ? '.' : ',';

            const before = original.slice(0, sepPos);
            const after = original.slice(sepPos + 1);

            // Heuristic #1: If the part after has 2 digits => decimal
            if (after.length === 2) {
                decimalIndex = sepPos;
                decimalSeparator = sepChar;
                // Build "cleaned"
                if (sepChar === ',') {
                    // remove commas => we only have 1 comma anyway
                    // but also remove any leftover dots that might be thousands
                    cleaned = original.replace(/\./g, '');
                    // then replace the actual decimal ',' with '.'
                    // but be careful only to replace the one at sepPos or all?
                    // simpler to do: remove all '.' first, then replace ',' => '.'
                    cleaned = cleaned.replace(',', '.');
                } else {
                    // '.' is decimal => remove all commas
                    cleaned = original.replace(/,/g, '');
                }
            }
            // Heuristic #2: If the part after has 3 digits => thousands
            else if (after.length === 3 && before.length >= 1) {
                // This means we interpret the single separator as thousands => no decimal
                decimalIndex = -1; // no decimal
                decimalSeparator = null;
                // remove that separator from the cleaned version
                cleaned = original.replace(/[.,]/g, '');
            }
            else {
                // Otherwise => decimal
                decimalIndex = sepPos;
                decimalSeparator = sepChar;

                if (sepChar === ',') {
                    // remove all '.' => thousands
                    cleaned = original.replace(/\./g, '');
                    // replace ',' => '.'
                    cleaned = cleaned.replace(',', '.');
                } else {
                    // '.' => remove all commas
                    cleaned = original.replace(/,/g, '');
                }
            }
        }
    }

    // Now parse the cleaned string to get floatValue
    const floatValue = parseFloat(cleaned);

    // Split into mainNumber and decimalPart
    let mainNumber = '';
    let decimalPart = '';

    if (decimalIndex === -1 || decimalIndex >= original.length) {
        // no decimal
        mainNumber = original;
        decimalPart = '';
    } else {
        mainNumber = original.slice(0, decimalIndex);
        decimalPart = original.slice(decimalIndex); // includes the separator
    }

    return {
        floatValue,
        mainNumber,
        decimalPart,
        decimalSeparator
    };
}

function splitPrice(price) {
    if (typeof price !== 'string') {
        return
    }
    let parsed = extractCurrencyAndAmount(price)
    if (!parsed || !parsed.currency || !parsed.amount || !parsed.originalNumber) {
        return
    }

    price = price.replace(parsed.originalNumber, '').replace(parsed.currency, '_SEP_').trim()
    // Get before and after
    let parts = price.split('_SEP_')
    let before = parts[0].trim()
    let after = parts[1].trim()
    let result = {
        before: before,
        after: after,
    }
    let number = {
        left: null,
        main: parsed.mainNumber,
        right: null,
    }
    // Add decimal and currency to right places
    if (parsed.position === 'before') {
        number.left = parsed.currency
        number.right = parsed.decimalPart
    } else {
        number.right = parsed.decimalPart ? parsed.decimalPart : ''
        number.right += parsed.currency
    }
    result.number = number
    return result
}
/*
const examples = [
    "Por apenas R$ 45,99 à vista",   // 'R$' + "45,99"
    "Preço: 45,99 €",                // "45,99" + '€'
    "Only $12.50",                   // '$' + "12.50"
    "15.99 USD",                     // "15.99" + 'USD'
    "Apenas 1.200,50 BRL",           // "1.200,50" => 1200.50
    "100 GBP",                       // "100" + 'GBP'
    "Preço 12,345.67 ¥",             // "12,345.67" => 12345.67
    "El café cuesta Col$ 5.000",     // "Col$" + "5.000" => 5000
    "¿Cuánto cuesta? Mex$ 123,45",   // "Mex$" + "123,45" => 123.45
    "Vendo por Ar$ 789.01",          // "Ar$" + "789.01" => 789.01
    "Cambio RD$ 3000",               // "RD$" + "3000" => 3000
    "S/. 250",                       // "S/." + "250" => 250
    "Bs. 350,50",                    // "Bs." + "350,50" => 350.50
    "Invalid text with no currency", // => null
    "1,234"                          // single comma, interpreted as decimal => 1.234
];

examples.forEach(str => {
    const result = extractCurrencyAndAmount(str);
    console.log(str, "=>", result);
});*/
